/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterContentInit, ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  Renderer2,
  Self,
  SimpleChanges,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';

import { TranslationService } from '@gh/core-mls';
import { getScrollbarHeight, getScrollbarWidth, notNil } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';

import {
  AllCommunityModule,
  CellDoubleClickedEvent,
  CellEditingStartedEvent,
  createGrid,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  ModuleRegistry,
  _processOnChange,
  themeMaterial,
} from 'ag-grid-community';
import type { DataTypeDefinitions, GetRowIdParams } from 'ag-grid-community';
import { AngularFrameworkComponentWrapper, AngularFrameworkOverrides } from 'ag-grid-angular';
import { assign, find, isEmpty, isString, map, noop, over, property, remove, sortBy } from 'lodash';
import type { RowDataIdGetter } from '../../features/ext/grid-options-ext';
import {
  GRID_FEATURE,
  GridFeature,
  GridFeatureReadyParams,
  GridNoRowsOverlayDirective,
} from '../../features';
import { Subject } from 'rxjs';

const GRID_INPUTS_NOT_GRID_OPTIONS = new Set([
  'pinnedTopData',
  'pinnedBottomData',
  'theme',
  'rowClass',
  'dragRow',
  'selectable',
  'selectionPrimaryKey',
  'hasSettings',
  'sorting',
  'useNoRowsOverlay',
  'noRowsTitle',
  'idProperty',
  'headless',
  'sizeColumnsToFit',
  'definition',
  'loading',
  'enterNavigatesVertically',
  'enterNavigatesVerticallyAfterEdit',
]);
import { GridRowEvent } from '../../grid-events';
import { ICON_SET } from '../../icon-set';
import { GridLayout, GridDefinition, GridDefinitionManager } from '../../layout';
import { GridColumnComponent } from '../grid-column/grid-column.component';
import { GridOptionBarComponent } from '../grid-option-bar/grid-option-bar.component';
import { GridSelectBarComponent } from '../grid-select-bar/grid-select-bar.component';
import { createLoadingOverlay, createNoRowsOverlay } from './grid-overlay-definitions';

const THEME_CONFIG: Record<string, { headerHeight: number; rowHeight: number }> = {
  typical: {
    headerHeight: 40,
    rowHeight: 35,
  },
  narrow: {
    headerHeight: 31,
    rowHeight: 26,
  },
  sparsed: {
    headerHeight: 60,
    rowHeight: 55,
  },
  todo: {
    headerHeight: 60,
    rowHeight: 55,
  },
};

const GH_AG_GRID_ROW_HOVER_VAR = 'var(--gh-ag-grid-row-hover-color)';
const GH_AG_GRID_SELECTED_ROW_BG_VAR = 'var(--gh-ag-grid-selected-row-bg-color)';

function resolveThemeConfig(theme: string): { headerHeight: number; rowHeight: number } {
  return THEME_CONFIG[theme] ?? THEME_CONFIG.typical;
}

function buildMaterialTheme(theme: string) {
  const { rowHeight } = resolveThemeConfig(theme);
  return themeMaterial.withParams({
    rowHeight,
    rowHoverColor: GH_AG_GRID_ROW_HOVER_VAR,
    selectedRowBackgroundColor: GH_AG_GRID_SELECTED_ROW_BG_VAR,
  });
}

const GH_GRID_DATA_TYPE_DEFINITIONS: DataTypeDefinitions = {
  object: {
    baseDataType: 'object',
    extendsDataType: 'object',
    valueFormatter: ({ value }) => {
      if (value == null) {
        return '';
      }
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch {
          return Object.prototype.toString.call(value);
        }
      }
      return String(value);
    },
  },
};

function resolveRowDataIdGetter(idProperty: string | RowDataIdGetter): RowDataIdGetter {
  return isString(idProperty) ? property(idProperty) : idProperty;
}

let communityModulesRegistered = false;

@Component({
  standalone: false,
  selector: 'gh-grid',
  templateUrl: 'grid.component.html',
  styleUrls: ['grid.component.scss'],
  providers: [
    AngularFrameworkOverrides,
    AngularFrameworkComponentWrapper,
    GridDefinitionManager,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'ghGrid',
})
export class GridComponent implements AfterContentInit, OnChanges, OnInit, OnDestroy {

  @Input() theme = 'typical';
  @Input() rowClass: any;
  @Input() dragRow = false;
  @Input() selectable = false;
  @Input() selectionPrimaryKey = 'id';
  @Input() hasSettings = true;
  @Input() sorting = true;
  @Input() useNoRowsOverlay = true;
  @Input() noRowsTitle = this.translationService.get('lbl_you_have_no_entries');
  @Input() idProperty?: string | RowDataIdGetter;
  @Input() headless = false;
  @Input() enterNavigatesVertically = true;
  @Input() enterNavigatesVerticallyAfterEdit = true;

  @Output() rowClicked = new EventEmitter<GridRowEvent<any>>();
  @Output() cellDoubleClicked = new EventEmitter<CellDoubleClickedEvent>();
  @Output() cellEditingStarted = new EventEmitter<CellEditingStartedEvent>();
  @Output() gridReady = new EventEmitter<GridReadyEvent>();
  @Output() layoutChange: EventEmitter<GridLayout>;

  @ContentChildren(GridColumnComponent) columns: QueryList<GridColumnComponent>;
  @ContentChild(GridOptionBarComponent, { static: false }) optionBar: GridOptionBarComponent;
  @ContentChild(GridSelectBarComponent, { static: false }) selectBar: GridSelectBarComponent;
  @ContentChild(GridNoRowsOverlayDirective, { static: false }) noRowsOverlay?: GridNoRowsOverlayDirective;

  @HostBinding('class.stretch-container')
  classStretchContainer = true;

  initialized: boolean = false;
  api: GridApi;
  ready$$: Subject<any> = new Subject();
  selectedRows: any[] = [];

  columnApi: GridApi;

  onReady = this.ready$$.asObservable().pipe(map$(() => ({ api: this.api, columnApi: this.columnApi })));

  private _sizeColumnsToFit: boolean;
  private options: GridOptions;
  private destroyed: boolean = false;
  private gridEl: HTMLElement;
  private _pinnedTopData: any[];
  private _pinnedBottomData: any[];
  private _loading: boolean = false;

  private bodyEl: HTMLElement;
  private overlayEl: HTMLElement;

  constructor(public elementRef: ElementRef,
              private viewContainerRef: ViewContainerRef,
              private renderer: Renderer2,
              private angularFrameworkOverrides: AngularFrameworkOverrides,
              private angularFrameworkComponentWrapper: AngularFrameworkComponentWrapper,
              private translationService: TranslationService,
              private definitionManager: GridDefinitionManager,
              @Optional() @Self() @Inject(GRID_FEATURE) private features: GridFeature[] = []) {
    if (!this.features) {
      this.features = [];
    }
    this.layoutChange = this.definitionManager.layoutChange;
  }

  @Input()
  set sizeColumnsToFit(sizeColumnsToFit: any) {
    this._sizeColumnsToFit = coerceBooleanProperty(sizeColumnsToFit);
  }

  @Input()
  set definition(definition: GridDefinition) {
    this.definitionManager.update(definition);

    if (this.initialized) {
      this.mergeDefinition();
    }
  }

  @Input()
  set pinnedTopData(data: any[]) {
    if (this.api) {
      this.api.setGridOption('pinnedTopRowData', data);

      this.fixOverlayPosition();
    }
    this._pinnedTopData = data;
  }

  @Input()
  set pinnedBottomData(data: any[]) {
    if (this.api) {
      this.api.setGridOption('pinnedBottomRowData', data);

      this.fixOverlayPosition();
    }
    this._pinnedBottomData = data;
  }

  @Input()
  set loading(loading: boolean) {
    this._loading = loading;

    if (this.api) {
      this.updateLoadingOverlay();
    }
  }

  get displaySettings(): boolean {
    return this.definitionManager.dynamicLayout && this.hasSettings && this.definitionManager.hasLayout;
  }

  ngOnInit(): void {
    this.gridEl = this.elementRef.nativeElement.querySelector('#grid');

    this.renderer.addClass(this.elementRef.nativeElement, 'gh-ag-grid');
    this.renderer.addClass(this.elementRef.nativeElement, `gh-grid-${this.theme}-theme`);
    if (this.headless) {
      this.renderer.addClass(this.elementRef.nativeElement, `gh-grid-headless`);
    }
    if (this.selectable) {
      this.renderer.addClass(this.elementRef.nativeElement, 'selectable');
    }
  }

  ngOnDestroy(): void {
    if (this.initialized) {
      this.destroyFeatures();
      this.definitionManager.destroy();
      this.destroyed = true;
      this.api.destroy();
      this.ready$$.complete();
    }
  }

  ngAfterContentInit(): void {
    if (!communityModulesRegistered) {
      ModuleRegistry.registerModules([AllCommunityModule]);
      communityModulesRegistered = true;
    }

    this.definitionManager.columns = this.columns.map((c) => c);
    this.initFeatures();
    const themeConfig = resolveThemeConfig(this.theme);
    this.options = {
      theme: buildMaterialTheme(this.theme),
      headerHeight: themeConfig.headerHeight,
      rowHeight: themeConfig.rowHeight,
      getRowClass: this.rowClass,
      suppressMovableColumns: true,
      rowDragManaged: this.dragRow,
      animateRows: true,
      defaultColDef: {
        resizable: true,
        sortable: this.sorting,
      },
      getRowId: this.idProperty
        ? (params: GetRowIdParams) =>
            String(resolveRowDataIdGetter(this.idProperty as string | RowDataIdGetter)(params.data))
        : undefined,
      rowData: [],
      icons: {
        groupExpanded: ICON_SET.GROUP_EXPANDED,
        groupContracted: ICON_SET.GROUP_CONTRACTED,
        sortAscending: ICON_SET.SORT_ASCENDING,
        sortDescending: ICON_SET.SORT_DESCENDING,
        externalFilter: ICON_SET.EXTERNAL_FILTER,
      },
      overlayNoRowsTemplate: this.useNoRowsOverlay ? createNoRowsOverlay(this.noRowsTitle) : ' ',
      overlayLoadingTemplate: createLoadingOverlay(),
      suppressAnimationFrame: true,
      stopEditingWhenCellsLoseFocus: false,
      enterNavigatesVertically: this.enterNavigatesVertically,
      enterNavigatesVerticallyAfterEdit: this.enterNavigatesVerticallyAfterEdit,
      onGridReady: this.onGridReady.bind(this),
    };

    this.mergeDefinition();

    this.enhanceOptions();

    const rowModelType = this.options.rowModelType;
    if (rowModelType && rowModelType !== 'clientSide') {
      this.options.rowData = undefined;
    }

    this.angularFrameworkOverrides.runOutsideAngular(() => {
      this.angularFrameworkComponentWrapper.setViewContainerRef(
        this.viewContainerRef,
        this.angularFrameworkOverrides,
      );
      const api = createGrid(this.gridEl, this.options, {
        globalListener: this.gridGlobalListener.bind(this),
        frameworkOverrides: this.angularFrameworkOverrides,
        providedBeanInstances: {
          frameworkCompWrapper: this.angularFrameworkComponentWrapper,
        },
        modules: [AllCommunityModule],
        setThemeOnGridDiv: true,
      } as unknown as Parameters<typeof createGrid>[2]);
      if (api) {
        this.api = api;
      }
    });

    if (this.selectable) {
      this.ready$$.subscribe(() => {
        this.api.addEventListener('rowClicked', (event: any) => {
          if (event.event.shiftKey) {
            this.selectRow(event.data);
          }
        });
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && this.api) {
      this.angularFrameworkOverrides.runOutsideAngular(() => {
        const gridOptions: Record<string, unknown> = {};
        for (const key of Object.keys(changes)) {
          if (GRID_INPUTS_NOT_GRID_OPTIONS.has(key)) {
            continue;
          }
          gridOptions[key] = changes[key].currentValue;
        }
        if (Object.keys(gridOptions).length > 0) {
          _processOnChange(gridOptions, this.api);
        }
        if (changes.enterNavigatesVertically || changes.enterNavigatesVerticallyAfterEdit) {
          this.api.updateGridOptions({
            enterNavigatesVertically: this.enterNavigatesVertically,
            enterNavigatesVerticallyAfterEdit: this.enterNavigatesVerticallyAfterEdit,
          });
        }
      });
    }
  }

  openSettings(): void {
    this.definitionManager.openSettings();
  }

  onGridReady(event: GridReadyEvent): void {
    const api = notNil(event.api);
    assign(this, { api, columnApi: api, initialized: true });
    this.definitionManager.init(api, api as any);
    const readyParams: GridFeatureReadyParams = {
      api,
      columnApi: api as any,
      sharedBeans: undefined as any,
    };
    this.readyFeatures(readyParams);
    this.ready$$.next(this);

    this.wireGridEventEmitters();

    this.gridReady.emit(event);

    this.bodyEl = this.elementRef.nativeElement.querySelector('.ag-body-viewport');
    this.overlayEl = this.elementRef.nativeElement.querySelector('.ag-overlay');

    if (this._pinnedTopData) {
      api.setGridOption('pinnedTopRowData', this._pinnedTopData);
    }

    if (this._pinnedBottomData) {
      api.setGridOption('pinnedBottomRowData', this._pinnedBottomData);
    }

    if (this._sizeColumnsToFit) {
      api.sizeColumnsToFit(this.getPreferredBodyWidth());
    }

    if (this.headless) {
      api.setGridOption('headerHeight', 0);
    }

    if (this._loading) {
      this.updateLoadingOverlay();
    }

    this.fixOverlayPosition();
  }

  private gridGlobalListener(_eventType: string, _event?: unknown): void {
    // no-op
  }

  private wireGridEventEmitters(): void {
    const safe = <T>(emitter: EventEmitter<T>, value: T) => {
      if (!this.destroyed) {
        emitter.emit(value);
      }
    };
    this.api.addEventListener('rowClicked', (e: any) => safe(this.rowClicked, e));
    this.api.addEventListener('cellDoubleClicked', (e: any) => safe(this.cellDoubleClicked, e));
    this.api.addEventListener('cellEditingStarted', (e: any) => safe(this.cellEditingStarted, e));
  }

  selectRow(item: any): void {
    const { selectionPrimaryKey } = this;
    const selectedPrimaryKeys = map(this.selectedRows, ({ [selectionPrimaryKey]: id }) => id);
    this.api.forEachNodeAfterFilter((node: IRowNode) => {
      if (node.data[selectionPrimaryKey] === item[selectionPrimaryKey]) {
        const alreadySelected = selectedPrimaryKeys.includes(node.data[selectionPrimaryKey]);
        if (alreadySelected) {
          remove(this.selectedRows, ({ [selectionPrimaryKey]: id }: any) => id === node.data[selectionPrimaryKey]);
        } else {
          this.selectedRows.push(node.data);
        }
        node.setSelected(!alreadySelected);
      }
    });
  }

  selectAllRows(): void {
    this.api.selectAll();
    assign(this, { selectedRows: this.api.getSelectedRows() });
  }

  deselectAllRows(): void {
    this.api.deselectAll();
    assign(this, { selectedRows: [] });
  }

  isRowSelected(item: any): boolean {
    const { selectionPrimaryKey } = this;
    return !!find(this.selectedRows, (row: any) => row.data[selectionPrimaryKey] === item[selectionPrimaryKey]);
  }

  isSelectedPartial(): boolean {
    if (!this.api) {
      return false;
    }
    return !isEmpty(this.selectedRows)
      && this.selectedRows.length !== this.api.getDisplayedRowCount();
  }

  isSelectedAll(): boolean {
    if (!this.api) {
      return false;
    }
    return !isEmpty(this.selectedRows) && !this.isSelectedPartial();
  }

  private initFeatures(): void {
    if (this.optionBar) {
      this.features.push(this.optionBar.getFeature());
    }

    if (this.selectBar) {
      this.features.push(this.selectBar.getFeature());
    }

    if (this.noRowsOverlay) {
      this.features.push(this.noRowsOverlay.getFeature());
    }

    this.features = sortBy(this.features, (feature) => feature.order || 0);

    this.features.forEach((feature) => feature.initGrid({
      viewContainerRef: this.viewContainerRef,
      nativeElement: this.gridEl,
    }));
  }

  private enhanceOptions(): void {
    const { features } = this;
    if (!isEmpty(features)) {
      const compactFeatureProcesses = (feature: GridFeature) =>
        (params: any) => feature.process ? feature.process(params) : noop;
      const processRowPostCreate = over(map(features, compactFeatureProcesses), <any>this.options.processRowPostCreate);
      assign(this.options, { processRowPostCreate });
    }
    this.features.forEach((feature) => feature.enhanceGridOptions(this.options));
    this.definitionManager.afterGridOptionsEnhanced(this.options);
    assign(this.options, {
      theme: buildMaterialTheme(this.theme),
      dataTypeDefinitions: {
        ...GH_GRID_DATA_TYPE_DEFINITIONS,
        ...(this.options.dataTypeDefinitions ?? {}),
      },
    });
  }

  private readyFeatures(params: GridFeatureReadyParams): void {
    this.features.forEach((feature) => feature.onGridReady(params));
  }

  private destroyFeatures(): void {
    this.features.forEach((feature) => feature.onDestroy());
  }

  private mergeDefinition(): void {
    const options = this.definitionManager.createOptions();
    assign(this.options, { ...options, defaultColDef: { ...this.options.defaultColDef, ...options.defaultColDef } });

    if (this.api) {
      this.api.updateGridOptions({ columnDefs: options.columnDefs as any });
    }
  }

  private updateLoadingOverlay(): void {
    const { _loading: loading } = this;

    this.api.setGridOption('loading', loading);
  }

  /**
   * Overlay should not overlap pinned rows
   */
  private fixOverlayPosition(): void {
    const bottomPinnedPanelHeight = this.api.getPinnedBottomRowCount() * resolveThemeConfig(this.theme).rowHeight
      + getScrollbarHeight();
    this.overlayEl.style.bottom = `${bottomPinnedPanelHeight}px`;
  }

  /**
   * Returns preferred width of the body.
   */
  private getPreferredBodyWidth(): number {
    const vScrollbarWidth = this.options.scrollbarWidth ? this.options.scrollbarWidth : getScrollbarWidth();
    return this.bodyEl.clientWidth - vScrollbarWidth;
  }
}

export class GridSharedBeans {}
